import sizings from 'widget_main/styles/sizings.scss';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { isShiftKey, isTabKey } from 'widget_main/globals/keyboardEvents';

const MOBILE_SCREEN_WIDTH = Number(sizings.mobileScreenWidth.slice(0, -2));
const MOBILE_SCREEN_HEIGHT = Number(sizings.mobileScreenHeight.slice(0, -2));

export const isMobileViewport = () => {
  const isMobile =
    window.parent.innerWidth <= MOBILE_SCREEN_WIDTH ||
    window.parent.innerHeight <= MOBILE_SCREEN_HEIGHT;

  return isMobile;
};

export type FocusableElement =
  | HTMLInputElement
  | HTMLButtonElement
  | HTMLAnchorElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

// For accessibility purposes, we want to lock focus within the widget and modals while it is active
// for customers using a keyboard or accessibility tools
export const useFocusLock = <T extends HTMLElement>() => {
  const [ref, updateRef] = useState<T | null>(null);

  const eventListener = useCallback(
    (e: KeyboardEvent) => {
      if (!ref || !isTabKey(e)) {
        return;
      }
      const focusableElements =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusableContent = ref.querySelectorAll(focusableElements);
      const firstFocusableElement = focusableContent[0] as FocusableElement;
      const lastFocusableElement = focusableContent[
        focusableContent.length - 1
      ] as FocusableElement;

      // if shift key is pressed for shift + tab combination
      if (isShiftKey(e)) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus(); // add focus for the last focusable element
          e.preventDefault();
        }
      } else if (document.activeElement === lastFocusableElement) {
        // if focused has reached the last focusable element then focus first focusable element after pressing tab
        firstFocusableElement.focus();
        e.preventDefault();
      }
    },
    [ref],
  );

  useEffect(() => {
    if (ref) {
      ref.addEventListener('keydown', eventListener);
    }
    return () => {
      ref?.removeEventListener('keydown', eventListener);
    };
  }, [ref]);

  return updateRef;
};
